import React, { FC, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { createUseStyles } from "react-jss";
import clsx from "clsx";

import InstagramObject from "Components/InstagramObject";
import MediaIcon from "Components/MediaIcon";

import RightArrow from "Assets/images/right_arrow.svg";

import ModalClose from "Assets/images/modal_close.svg";
import Video from "Assets/images/video_icon.svg";
import Pano from "Assets/images/pano_icon.svg";
import Photo from "Assets/images/photo_icon.svg";
import Instagram from "Assets/images/instagram.svg";
import CampusReel from "Assets/images/campusreels.svg";
import { motion } from "framer-motion";
import { getOneTrustSafeImgSrc } from "Utils/utils";
import styles from "./styles";

interface Props {
  media: Array<Object>;
  selectedIdx: number;
  setIdx: (idx: number) => void;
  children: any;
  showMediaViewer: any;
  setShowMediaViewer: (val: any) => void;
  handleCloseMediaViewer: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  fullScreen: boolean; // used in styles.ts
  errorIdx: any;
  setErrorIdx: (idx: any) => void;
}

const getIcon = (mediaType: string, network: string, type: string) => {
  if (network === "instagram") return Instagram;
  if (type === "campusreel") return CampusReel;
  switch (mediaType) {
    case "video":
      return Video;
    case "panorama":
      return Pano;
    case "photo":
      return Photo;
    default:
      return null;
  }
};

const MediaViewer: FC<Props> = (props: Props) => {
  const useStyles = createUseStyles(styles);
  const dispatch = useDispatch();
  const classes = useStyles({ ...props });
  const {
    media,
    setIdx,
    selectedIdx,
    children,
    showMediaViewer,
    setShowMediaViewer,
    handleCloseMediaViewer,
    setErrorIdx,
    errorIdx,
  } = props;

  const sceneAnimations = {
    open: {
      y: "0",
      opacity: 1,
      transition: {
        ease: [0.28, 0.57, 0.2, 1],
        duration: 0.5,
        delay: 0.5,
      },
    },
    closed: {
      y: "-100px",
      opacity: 0,
      transition: {
        ease: "easeIn",
        duration: 0.25,
      },
    },
    openBG: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    closeBG: {
      opacity: 0,
      transition: {
        duration: 0.5,
        delay: 0.25,
        onComplete: () => {
          dispatch(setShowMediaViewer(null));
        },
      },
    },
  };

  const carouselRef = useRef(null);

  useEffect(() => {
    const keyDownListener = (e: any) => {
      if (e.keyCode === 27) dispatch(setShowMediaViewer(false));
    };
    document.addEventListener("keydown", keyDownListener);
    return () => document.removeEventListener("keydown", keyDownListener);
  }, [dispatch, setShowMediaViewer]);

  useEffect(() => {
    // Focus on first item in carousel //
    const firstItem = document.getElementById("carouselImages-0");
    if (firstItem) firstItem.focus();
  }, []);

  const handleClick = (idx: any) => {
    dispatch(setIdx(idx));
    let elmtitle = document.getElementById(`carousel-title`);
    if (elmtitle) {
      elmtitle.focus();
    } else {
      const timeout = setTimeout(() => {
        elmtitle = document.getElementById(`carousel-title`);
        if (elmtitle) {
          elmtitle.focus();
          clearTimeout(timeout);
        }
      }, 10);
    }
  };

  return (
    <>
      <motion.div
        initial={{ y: "100px", opacity: 0 }}
        animate={showMediaViewer ? "open" : "closed"}
        variants={sceneAnimations}
        className={classes.background}
        data-testid="background"
      >
        <div className={classes.container} data-testid="container">
          <button
            id="closeModal"
            type="button"
            aria-label="Close"
            className={classes.modalClose}
            onClick={handleCloseMediaViewer}
            data-testid="closeButton"
          >
            <ModalClose />
          </button>

          {children}
          <div
            className={classes.carousel}
            ref={carouselRef}
            data-testid="carousel"
          >
            {media.map((obj: any, idx: number) => {
              return (
                <div
                  key={obj.largeurl}
                  className={classes.imgContainer}
                  data-testid={`carouselImages-${idx}`}
                >
                  <button
                    id={`carouselImages-${idx}`}
                    tabIndex={obj.network === "instagram" ? -1 : 0}
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      handleClick(idx);
                    }}
                    className={classes.carouselImgButton}
                    onBlur={() => {
                      if (media.length - 1 === idx) {
                        const close = document.getElementById("closeModal");
                        if (close) close.focus();
                      }
                    }}
                  >
                    {obj.network === "instagram" ? (
                      <InstagramObject
                        embedId={
                          obj.mediaType === "photo"
                            ? obj.url
                            : obj.videoid || ""
                        }
                        alt={obj.title}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(idx);
                        }}
                        height={98}
                        width={160}
                        iframeWidth={obj.mediaType === "photo" ? "100%" : 280}
                        top={-62}
                        left={obj.mediaType === "photo" ? 0 : -60}
                        iframeHeight="163%"
                        className={{
                          [classes.mediaHighlight]: idx === selectedIdx,
                        }}
                      />
                    ) : obj.type === "campusreel" ? (
                      <iframe
                        src={obj.url}
                        title={obj.title}
                        tabIndex={-1}
                        width="160"
                        height="98"
                        className={clsx(classes.img, classes.iframe, {
                          [classes.mediaHighlight]: idx === selectedIdx,
                        })}
                        allowFullScreen
                      />
                    ) : (
                      <img
                        src={getOneTrustSafeImgSrc(obj.largeurl)}
                        aria-label={obj.title}
                        alt={obj.title}
                        className={clsx(classes.img, {
                          [classes.mediaHighlight]: idx === selectedIdx,
                        })}
                        onError={() => setErrorIdx([...errorIdx, obj.id])}
                      />
                    )}
                  </button>
                  <div
                    className={classes.thumbnailIcon}
                    data-testid="thumbnailImages"
                  >
                    <MediaIcon
                      Icon={getIcon(obj.mediaType, obj.network, obj.type)}
                      showBackground={false}
                      className={
                        obj.type === "campusreel" ? classes.campusReelIcon : ""
                      }
                    />
                  </div>
                </div>
              );
            })}
            {media.length > 7 && (
              <>
                <button
                  type="button"
                  aria-label="View previous media"
                  className={classes.leftArrow}
                  onClick={() => {
                    if (selectedIdx !== 0) {
                      dispatch(setIdx(selectedIdx - 1));
                      carouselRef.current.scrollBy(-160, 0);
                    }
                  }}
                >
                  <RightArrow />
                </button>
                <button
                  type="button"
                  aria-label="View next media"
                  className={classes.rightArrow}
                  onClick={() => {
                    if (selectedIdx !== media.length - 1) {
                      dispatch(setIdx(selectedIdx + 1));
                      carouselRef.current.scrollBy(160, 0);
                    }
                  }}
                >
                  <RightArrow />
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={showMediaViewer ? "openBG" : "closeBG"}
        variants={sceneAnimations}
        className={classes.darkBackground}
      />
    </>
  );
};

MediaViewer.defaultProps = {};

export default MediaViewer;
